import { Howl } from 'howler';

/**
 * 音频管理器 - 单例模式
 * 用于集中管理音频播放，避免多个音频同时播放
 */
class AudioManager {
  private static instance: AudioManager;
  private currentAudio: Howl | null = null;
  private isLoading: boolean = false;
  private debounceTimer: NodeJS.Timeout | null = null;
  private lastSrc: string = '';

  private constructor() {}

  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * 创建并播放音频（带防抖功能）
   * @param src 音频源
   * @param options 音频选项
   * @returns 创建的 Howl 实例
   */
  public createAudio(src: string, options: any = {}): Howl {
    // 如果是同一个音频源且正在加载或播放中，直接返回当前实例
    if (this.lastSrc === src && this.currentAudio) {
      return this.currentAudio;
    }
    
    // 记录当前音频源
    this.lastSrc = src;
    
    // 清除之前的防抖定时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    // 停止并卸载当前音频
    this.stopAndUnloadCurrentAudio();
    
    // 设置加载状态
    this.isLoading = true;
    
    // 创建新的音频实例
    const audio = new Howl({
      src: [src],
      html5: true,
      ...options,
      onload: () => {
        this.isLoading = false;
        if (options.onload) {
          options.onload();
        }
      },
      onloaderror: (id, error) => {
        this.isLoading = false;
        if (options.onloaderror) {
          options.onloaderror(id, error);
        }
      }
    });
    
    // 设置为当前音频
    this.currentAudio = audio;
    
    return audio;
  }

  /**
   * 带防抖的创建音频方法
   * @param src 音频源
   * @param options 音频选项
   * @param debounceTime 防抖时间（毫秒）
   * @returns Promise<Howl>
   */
  public createAudioDebounced(src: string, options: any = {}, debounceTime: number = 300): Promise<Howl> {
    return new Promise((resolve) => {
      // 清除之前的防抖定时器
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // 设置新的防抖定时器
      this.debounceTimer = setTimeout(() => {
        const audio = this.createAudio(src, options);
        resolve(audio);
      }, debounceTime);
    });
  }

  /**
   * 停止并卸载当前音频
   */
  public stopAndUnloadCurrentAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.stop();
      this.currentAudio.unload();
      this.currentAudio = null;
    }
  }

  /**
   * 检查是否正在加载音频
   */
  public isAudioLoading(): boolean {
    return this.isLoading;
  }

  /**
   * 获取当前音频实例
   */
  public getCurrentAudio(): Howl | null {
    return this.currentAudio;
  }
}

export default AudioManager.getInstance(); 